import type React from 'react';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { deepEqual } from 'fast-equals';

import usePrevious from './usePrevious';

import { productBundle } from 'apis/cloudcatalog';
import timedFetch from 'middleware/timedFetch';
import { fetchErrorMiddleware } from 'middleware/fetchErrorMiddleware';
import type { FormattedProductData } from 'reducers/detail/productDetail';
import { formatProductData } from 'reducers/detail/productDetail';
import { firePdpPixelServer } from 'actions/productDetail';
import useMartyContext from 'hooks/useMartyContext';
import type { ProductBundleOptions } from 'types/cloudCatalog';
import cloudCatalogUsingMerchantId from 'helpers/cloudCatalogUsingMerchantId';
import { trackError } from 'helpers/ErrorUtils';
import { VRSNL_MERCHANTID } from 'constants/crossSiteMerchantIdMapping';
import type { AppDispatch } from 'entrypoints/bootstrapOnClient';

// Accepts cloud catalog productBundle criteria and fetches whenever criteria deeply changes
const useProduct = (
  queryObject: ProductBundleOptions,
  merchantId?: string,
  initialColorId?: string
): [
  (FormattedProductData & { styleId: string }) | undefined,
  React.Dispatch<React.SetStateAction<(FormattedProductData & { styleId: string }) | undefined>>
] => {
  const [product, setProduct] = useState<FormattedProductData & { styleId: string }>();
  const previousInputs = usePrevious(queryObject);
  const dispatch: AppDispatch = useDispatch();
  let {
    environmentConfig: {
      api: { cloudcatalog }
    }
  } = useMartyContext();
  if (merchantId && merchantId === VRSNL_MERCHANTID) {
    cloudcatalog = cloudCatalogUsingMerchantId(merchantId);
  }

  useEffect(() => {
    async function fetchProduct() {
      if (product?.productId !== queryObject.productId) {
        const response = await fetchErrorMiddleware(await productBundle(cloudcatalog, queryObject, timedFetch('cloudCatalogProduct')));
        setProduct({
          ...formatProductData({ detail: response.product[0]! }, initialColorId),
          styleId: ''
        });
        if (!response.product[0]!.oos) {
          // OOS products don't have product links which the pixel server needs
          dispatch(firePdpPixelServer(response.product[0]!));
        }
      }
    }
    // since dep array of useEffect only shallow checks, we need to compare previous and current inputs so we don't infinitely fetch productBundle
    if (!deepEqual(previousInputs, queryObject)) {
      fetchProduct().catch(e => {
        trackError('NON-FATAL', 'Could not get Product details', e);
      });
    }
  });

  return [product, setProduct];
};

export default useProduct;
